import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography
} from '@mui/material';
import {
  Home,
  MenuBook,
  Create,
  Person,
  Dashboard,
  Event,
  Article,
  ExitToApp,
  People,
  EmojiEvents
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const handleSignOut = async () => {
    await logout();
    onClose();
    navigate('/');
  };

  // Helper function to get user's display name
  const getUserDisplayName = () => {
    if (!user) return '';
    
    // Try to get full name from given_name and family_name
    const firstName = user.given_name || user.attributes?.given_name || '';
    const lastName = user.family_name || user.attributes?.family_name || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (user.name) {
      return user.name;
    } else {
      // Fallback to a generic greeting without showing email
      return 'User';
    }
  };

  const menuItems = [
    { text: 'Home', icon: <Home />, path: '/' },
    // { text: 'Events', icon: <Event />, path: '/events' },
  ];

  const authenticatedItems = [
    { text: 'Browse Stories', icon: <MenuBook />, path: '/stories' },
    { text: 'Results', icon: <EmojiEvents />, path: '/results' },
    { text: 'Write Story', icon: <Create />, path: '/create' },
    { text: 'My Stories', icon: <MenuBook />, path: '/my-stories' },
    { text: 'Events', icon: <Event />, path: '/events' },
    { text: 'Profile', icon: <Person />, path: '/profile' },
  ];

  const adminItems = [
    { text: 'Manage Users', icon: <People />, path: '/admin' },
    { text: 'Manage Stories', icon: <Article />, path: '/admin/stories' },
    { text: 'Manage Events', icon: <Event />, path: '/admin/events' },
  ];

  const drawerContent = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          Short Stories
        </Typography>
        {isAuthenticated && user && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Welcome, {getUserDisplayName()}
          </Typography>
        )}
      </Box>
      
      <Divider />
      
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton onClick={() => handleNavigation(item.path)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {isAuthenticated && (
        <>
          <Divider />
          <List>
            {authenticatedItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton onClick={() => handleNavigation(item.path)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {isAuthenticated && user?.role === 'admin' && (
        <>
          <Divider />
          <List>
            {adminItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton onClick={() => handleNavigation(item.path)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {isAuthenticated && (
        <>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleSignOut}>
                <ListItemIcon><ExitToApp /></ListItemIcon>
                <ListItemText primary="Sign Out" />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}
    </Box>
  );

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
      sx={{
        '& .MuiDrawer-paper': {
          borderRadius: 0, // Remove rounded corners
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0
        }
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
